import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';

import { isNative } from '../lib/platform';

const CALLBACK_PREFIX = 'pt.powerfulconnect.app://auth/callback';

/**
 * Links já encaminhados nesta execução do app.
 *
 * 🔴 T1.19 (/code-review): `getLaunchUrl()` devolve o MESMO link de arranque durante toda a execução.
 * Sem esta memória, cada nova corrida do efeito (StrictMode, remontagem) reencaminhava ao callback um
 * link já consumido — que falha como "expirado" ou, com o token ainda válido, entra em ciclo.
 * Um link novo do e-mail traz tokens novos, logo um URL diferente.
 */
const encaminhados = new Set<string>();

/** Encaminha deep links de Auth para o mesmo callback web, sem colocar tokens na rota. */
export function NativeAuthCallbackListener() {
  const navigate = useNavigate();
  // `navigate` muda a cada troca de pathname (React Router 6): como dependência do efeito, o
  // listener seria removido e registado de novo, e o link de arranque relido, a cada navegação.
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!isNative()) return;
    let disposed = false;

    const routeCallback = (url: string | undefined) => {
      if (disposed || !url?.startsWith(CALLBACK_PREFIX) || encaminhados.has(url)) return;
      encaminhados.add(url);
      navigateRef.current('/auth/callback', { replace: true, state: { authCallbackUrl: url } });
    };

    const listener = App.addListener('appUrlOpen', ({ url }) => routeCallback(url));
    void App.getLaunchUrl().then((launch) => routeCallback(launch?.url));

    return () => {
      disposed = true;
      void listener.then((handle) => handle.remove());
    };
  }, []);

  return null;
}
